import styled, { css } from "styled-components";

/**
 * Wrapper do Cartão
 * Configura tamanho, sombra e transições com base no estado de seleção.
 */
export const StyledCard = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isSelected", "desktopVariant"].includes(prop),
})<{ isSelected: boolean; desktopVariant: boolean }>`
  box-shadow: ${({ theme, isSelected }) =>
    isSelected ? theme.shadows.light.medium : theme.shadows.light.small};
  border-radius: ${({ theme }) => theme.borderRadius.extraLarge};
  background-color: ${({ theme }) => theme.colors.white};
  width: ${({ theme, isSelected }) =>
    isSelected ? theme.spacing(50) : theme.spacing(44.15)};
  height: ${({ theme, isSelected }) =>
    isSelected ? theme.spacing(74) : theme.spacing(66)};
  transition: transform 0.5s ease-in-out;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  cursor: pointer;
  display: flex;

  ${({ desktopVariant }) =>
    desktopVariant &&
    `
      width: 17.25rem;
      height: 31.25rem;
      align-items: stretch;
      border-radius: 1rem;
      box-shadow: 0 2px 8px rgba(23, 65, 90, 0.12);
    `}

  &:hover {
    transform: scale(1.01);
    box-shadow: ${({ theme }) => theme.shadows.light.medium};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xxl}) {
    width: ${({ theme }) => theme.spacing(35)};
    height: ${({ theme }) => theme.spacing(60)};
    box-shadow: none;

    ${({ desktopVariant }) =>
      desktopVariant &&
      `
        width: 17.25rem;
        height: 31.25rem;
        box-shadow: 0 2px 8px rgba(23, 65, 90, 0.12);
      `}
  }
`;

/**
 * Wrapper da Imagem do Cartão
 * Estiliza a imagem de fundo e o badge de preço.
 */
export const CardImageWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isSelected", "desktopVariant"].includes(prop),
})<{ isSelected: boolean; desktopVariant: boolean }>`
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  position: relative;
  overflow: hidden;
  height: ${({ theme, isSelected }) =>
    isSelected ? theme.spacing(56) : theme.spacing(50)};
  width: 100%;

  ${({ desktopVariant }) =>
    desktopVariant &&
    `
      height: 21.1rem;
      overflow: visible;
      border-radius: 0;
      flex-shrink: 0;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.xxl}) {
    height: ${({ theme }) => theme.spacing(45)};

    ${({ desktopVariant }) => desktopVariant && `height: 21.1rem;`}
  }

  img {
    border-radius: ${({ theme, desktopVariant }) =>
      desktopVariant
        ? theme.borderRadius.medium
        : theme.borderRadius.extraLarge};
    height: ${({ theme, isSelected }) =>
      isSelected ? theme.spacing(54) : theme.spacing(48)};
    object-fit: cover;
    width: 100%;
    z-index: 0;

    ${({ desktopVariant }) =>
      desktopVariant &&
      `
        height: 21.1rem;
      `}

    @media (max-width: ${({ theme }) => theme.breakpoints.xxl}) {
      height: ${({ theme }) => theme.spacing(43)};

      ${({ desktopVariant }) => desktopVariant && `height: 21.1rem;`}
    }
  }

  ${({ desktopVariant }) =>
    !desktopVariant &&
    css<{ isSelected: boolean }>`
      & > div {
        border-radius: ${({ theme }) => theme.borderRadius.extraLarge};
        font-weight: ${({ theme }) => theme.fontWeights.bold};
        height: ${({ theme, isSelected }) =>
          isSelected ? theme.spacing(6) : theme.spacing(5)};
        width: ${({ theme, isSelected }) =>
          isSelected ? theme.spacing(26) : theme.spacing(22)};
        font-size: ${({ theme, isSelected }) =>
          isSelected ? theme.fontSizes.lg : theme.fontSizes.md};
        transform: translateX(-50%);
        justify-content: center;
        align-items: center;
        position: absolute;
        display: flex;
        bottom: 0;
        left: 50%;
        z-index: 1;
        background-color: ${({ theme }) => theme.colors.yellow};
        color: ${({ theme }) => theme.colors.black};

        @media (max-width: ${({ theme }) => theme.breakpoints.xxl}) {
          height: ${({ theme }) => theme.spacing(4)};
          width: ${({ theme }) => theme.spacing(18)};
          font-size: ${({ theme }) => theme.fontSizes.sm};
        }
      }
    `}
`;

export const PriceBanner = styled.div`
  position: absolute;
  right: 1.55rem;
  bottom: -1rem;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.05rem;
  padding: 0 0.75rem;
  border-radius: 0 1rem 1rem 0;
  background: ${({ theme }) => theme.colors.yellow};
  color: ${({ theme }) => theme.colors.black};
  white-space: nowrap;
`;

export const PriceLabel = styled.span`
  font-size: 0.76rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

export const PriceDivider = styled.span`
  width: 1px;
  height: 1rem;
  margin: 0 0.85rem;
  background: currentColor;
`;

export const PriceValue = styled.span`
  font-size: 1.06rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

export const DesktopCardBody = styled.div`
  width: 100%;
  padding: 1.75rem 1.7rem 0.65rem;
  text-align: left;
`;

export const DesktopCardHeading = styled.h3`
  margin: 0 0 0.1rem;
  color: ${({ theme }) => theme.colors.vividBlue};
  font-size: 1.1rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1.2;
`;

export const DesktopCardText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.darkBlue};
  font-size: 0.86rem;
  line-height: 1.35;
`;

export const DesktopCardFooter = styled.div`
  width: 100%;
  min-height: 4.75rem;
  padding: 0.65rem 1.35rem 0.75rem;
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.mediumGray};
  font-size: 0.8rem;
  line-height: 1.3;
  text-align: left;
`;

export const DesktopCardFooterLine = styled.p`
  margin: 0;
  white-space: nowrap;

  & + & {
    margin-top: 0.1rem;
  }
`;

/**
 * Conteúdo do Cartão
 * Estiliza texto e ícones no interior do cartão.
 */
export const CardContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isSelected"].includes(prop),
})<{ isSelected: boolean }>`
  padding-bottom: ${({ theme }) => theme.spacing(3.25)};
  flex-direction: column;
  align-items: center;
  text-align: center;
  display: flex;
`;
/**
 * Informações da Viagem
 */
export const CardTravelInfo = styled.p.withConfig({
  shouldForwardProp: (prop) => !["isSelected"].includes(prop),
})<{ isSelected: boolean }>`
  color: ${({ theme }) => theme.colors.black};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ isSelected }) => (isSelected ? "1.15rem" : "1rem")};

  @media (max-width: ${({ theme }) => theme.breakpoints.xxl}) {
    font-size: 0.85rem;
  }
`;

/**
 * Local da Viagem
 */
export const CardTravelLocal = styled.p.withConfig({
  shouldForwardProp: (prop) => !["isSelected"].includes(prop),
})<{ isSelected: boolean }>`
  color: ${({ theme }) => theme.colors.darkBlue};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ isSelected }) => (isSelected ? "1.2rem" : "1.05rem")};

  @media (max-width: ${({ theme }) => theme.breakpoints.xxl}) {
    font-size: 0.85rem;
  }
`;

/**
 * Detalhes da Viagem
 */
export const CardTravelDetails = styled.p`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  font-size: ${({ theme }) => theme.fontSizes.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.xxl}) {
    font-size: 0.6rem;
  }
`;

/**
 * Linha do Trajeto
 * Estiliza a linha contendo origem, destino e o ícone entre os dois.
 */
export const LineTravel = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
`;
